import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CodeSnippet01, IconClose1 } from "@velaro/velaro-component-lib";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import AccelaAccounts from "./AccelaAccounts";
import AccelaTabs from "./AccelaTabs";

interface AccelaConfig {
  id: number;
  agencyName: string;
  clientId: string;
  clientSecret: string;
  emailAddress: string;
  accessToken: string;
  refreshToken: string;
  scope?: string;
  teamId: number;
  token?: string;
  refreshTokenExpired?: boolean;
}

export default function AccelaCivic() {
  const api = useApi();
  const [configs, setConfigs] = useState<AccelaConfig[]>([]);
  const { teamId, updateTeamId } = useSelectedTeam();
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);

  async function handleSetup() {
    const response = await api.messaging.post("Accela/Auth/SaveCredentials", {
      tenantId: "tenantId",
      displayName: "displayName",
      company: "company",
      token: "token",
      tokenSecret: "tokenSecret"
    });

    const data = await response.json();
  }

  const fetchConfigs = useCallback(async () => {
    const resp = await api.messaging.get(`Accela/Integrations/`);
    const data = await resp.json();
    const now = new Date();
    const mapped = data.map((x: any) => ({
      id: x.id,
      agencyName: x.agencyName,
      clientId: x.clientId,
      clientSecret: x.clientSecret,
      emailAddress: x.emailAddress,
      accessToken: x.accessToken,
      refreshToken: x.refreshToken,
      teamId: x.teamId ?? 0,
      token: x.accessToken,
      refreshTokenExpired: x.refreshTokenExpireAt
        ? new Date(x.refreshTokenExpireAt) < now
        : false
    }));
    setConfigs(mapped);

    if (mapped.length === 1) {
      setSelectedConfigId(mapped[0].id);
    }
  }, [api.messaging]);

  const addConfig = useCallback(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const deleteConfig = useCallback(
    async (id: number) => {
      setConfigs((oldState) => {
        return oldState.filter((x) => x.id != id);
      });
      await api.messaging.delete(`Accela/Integrations/${id}`);
    },
    [api.messaging]
  );

  const handleConfigChange = (
    id: number,
    field: keyof AccelaConfig,
    value: string
  ) => {
    setConfigs((prevConfigs) =>
      prevConfigs.map((config) =>
        config.id === id ? { ...config, [field]: value } : config
      )
    );
  };

  const updateConfig = useCallback(
    (id: number, updatedConfig: Partial<AccelaConfig>) => {
      setConfigs((prevConfigs) =>
        prevConfigs.map((config) =>
          config.id === id ? { ...config, ...updatedConfig } : config
        )
      );
    },
    []
  );

  return (
    <div>
      <div className="mb-6">
        <AccelaAccounts
          configs={configs}
          deleteConfig={deleteConfig}
          addConfig={addConfig}
          teamId={teamId}
          onChange={handleConfigChange}
          onSelectConfig={setSelectedConfigId}
          updateConfig={updateConfig}
          refreshConfigs={fetchConfigs}
        />
      </div>
      <div className="mb-4">
        <AccelaTabs teamId={teamId!} selectedConfigId={selectedConfigId} />
      </div>
    </div>
  );
}

export function BackToIntegrationLanding() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
